import {
  Button,
  Card,
  DatePickerDropdown,
  HSpacer,
  Modal,
  Text,
  VSpacer,
} from '@/components/DesignSystem';
import { QueryKeys } from '@/constants/QueryKeys';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import AddIcon from '@mui/icons-material/Add';
import EditOutlined from '@mui/icons-material/EditOutlined';
import { Stack, useTheme } from '@mui/material';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';

interface HistoricalCutoffCardProps {
  historicalCutoffDate?: string | null,
  onChange: (historicalCutoffDate: string | null) => void,
  retailerId: string,
}

export const HistoricalCutoffCard = ({
  historicalCutoffDate,
  onChange,
  retailerId,
}: HistoricalCutoffCardProps) => {
  const theme = useTheme();
  const [showHistoricalCutoffModal, setShowHistoricalCutoffModal] = useState(false);
  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { mutate: applyHistoricalCutoff, isLoading: isApplying } = useMutation(
    () => RetailerApi.applyHistoricalCutoff(retailerId),
    {
      onSuccess: async () => {
        openSnackbar('Historical cutoff applied successfully');
        await queryClient.invalidateQueries(QueryKeys.GET_RETAILER_BY_ID);
      },
      onError: (error: Error) => {
        openSnackbar(error.message || 'An error has occurred');
      },
    },
  );

  const formattedDate = historicalCutoffDate
    ? DateTime.fromFormat(historicalCutoffDate, 'yyyy-MM-dd').toFormat('MM-dd-yyyy')
    : null;

  return (
    <>
      <Card elevation={0} testID="historical-cutoff-card">
        <Stack alignItems="center" direction="row" justifyContent="space-between">
          <Stack>
            <Stack alignItems="center" direction="row">
              <Text category="title-small">
                Historical Cutoff Date
              </Text>
              {!!historicalCutoffDate && (
                <Text category="title-small">
                  : {formattedDate}
                </Text>
              )}
            </Stack>
            <VSpacer size="2" />
            <Text category="body-medium" sx={{ color: theme.palette.text.secondary }}>
              Any customer that does not have a transaction with an order date more recent than
              this date will be regarded as 'historical' and will not receive notifications,
              be displayed in the app, or be counted in any KPIs.
            </Text>
          </Stack>
          <HSpacer size="3" />
          <Stack direction="row" spacing={1}>
            {historicalCutoffDate && (
              <Button
                color="primary"
                disabled={isApplying}
                onClick={() => applyHistoricalCutoff()}
                testID="apply-historical-cutoff-button"
                variant="outlined"
              >
                Apply
              </Button>
            )}
            <Button
              color="primary"
              onClick={() => setShowHistoricalCutoffModal(true)}
              startIcon={historicalCutoffDate ? <EditOutlined/> : <AddIcon/>}
              testID="historical-cutoff-button"
              variant="outlined"
            >
              {historicalCutoffDate ? 'Update' : 'Set historical cutoff'}
            </Button>
          </Stack>
        </Stack>
      </Card>
      {showHistoricalCutoffModal && (
        <HistoricalCutoffModal
          historicalCutoffDate={historicalCutoffDate}
          onClose={() => setShowHistoricalCutoffModal(false)}
          onSave={(newHistoricalCutoffDate) => onChange(newHistoricalCutoffDate)}
        />
      )}
    </>
  );
};


interface HistoricalCutoffModalProps {
  historicalCutoffDate?: string | null,
  onClose: () => void,
  onSave: (historicalCutoffDate: string | null) => void,
}

const HistoricalCutoffModal = ({
  historicalCutoffDate,
  onClose,
  onSave,
}: HistoricalCutoffModalProps) => {
  const initialDate = historicalCutoffDate
    ? DateTime.fromFormat(historicalCutoffDate, 'yyyy-MM-dd').toJSDate()
    : undefined;

  const [value, setValue] = useState<Date | undefined>(initialDate);
  const isEditing = !!historicalCutoffDate;

  const isValueChanged = value?.getTime() !== initialDate?.getTime();
  const isDateGreaterThanExisting = isEditing && value && historicalCutoffDate
    ? DateTime.fromJSDate(value).toFormat('yyyy-MM-dd') > historicalCutoffDate
    : false;
  const isValid = !!value && (!isEditing || isValueChanged) && !isDateGreaterThanExisting;

  const errorMessage = isDateGreaterThanExisting
    ? 'Date must be less than existing historical cutoff'
    : undefined;

  const handleSave = () => {
    if (value) {
      const formattedValue = DateTime.fromJSDate(value).toFormat('yyyy-MM-dd');
      onClose();
      onSave(formattedValue);
    }
  };

  return (
    <Modal
      acceptButton={(props) => (
        <Button
          {...props}
          disabled={!isValid}
          onClick={handleSave}
          testID="historical-cutoff-modal-save-button"
        >
          Save
        </Button>
      )}
      cancelButton={(props) => (
        <Button
          {...props}
          onClick={onClose}
          testID="historical-cutoff-modal-cancel-button"
        >
          Cancel
        </Button>
      )}
      onClose={onClose}
      open
      testID="historical-cutoff-modal"
      title={`${isEditing ? 'Update' : 'Set'} historical cutoff`}
    >
      <DatePickerDropdown
        errorMessage={errorMessage}
        label="Historical Cutoff Date"
        onChange={setValue}
        testID="historical-cutoff-date-picker"
        value={value}
      />
    </Modal>
  );
};
